import React, { useState } from 'react';
import {
  useCreateEmailTemplateMutation,
  useCreateIncomingEmailRuleMutation,
  useDeleteEmailTemplateMutation,
  useDeleteIncomingEmailRuleMutation,
  useGetEmailTemplatesQuery,
  useGetIncomingEmailRulesQuery,
  useUpdateEmailTemplateMutation,
  useUpdateIncomingEmailRuleMutation,
} from '@/store/emailInboxApi';
import { EmailTemplateItem, IncomingEmailRule } from '@/types/EmailInbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { ConfirmPopover } from '@/components/ui/ConfirmPopover';
import { HtmlSignatureEditor } from './HtmlSignatureEditor';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export function AutomationTab() {
  const { data: rules = [], isLoading: rulesLoading } = useGetIncomingEmailRulesQuery();
  const { data: templates = [], isLoading: templatesLoading } = useGetEmailTemplatesQuery();
  const [editRule, setEditRule] = useState<IncomingEmailRule | null>(null);
  const [editTemplate, setEditTemplate] = useState<EmailTemplateItem | null>(null);

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">כללי סינון אוטומטיים</h2>
            <p className="text-sm text-gray-500">התעלמות או שליחת תגובה אוטומטית לפי שולח, נושא ותוכן</p>
          </div>
          <Button onClick={() => setEditRule({
            id: 0,
            name: '',
            from_contains: '',
            subject_contains: '',
            body_contains: '',
            action: 'ignore',
            reply_template_key: '',
            is_active: true,
            priority: 100,
          })}>
            <Plus className="h-4 w-4 ml-1" />
            כלל חדש
          </Button>
        </div>

        {rulesLoading ? <div className="flex justify-center py-10"><Spinner /></div> : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-right">שם</th>
                  <th className="px-4 py-3 text-right">שולח</th>
                  <th className="px-4 py-3 text-right">נושא</th>
                  <th className="px-4 py-3 text-right">פעולה</th>
                  <th className="px-4 py-3 text-right">עדיפות</th>
                  <th className="px-4 py-3 text-right">פעיל</th>
                  <th className="px-4 py-3 text-right">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rules.map((rule) => (
                  <RuleRow key={rule.id} rule={rule} onEdit={setEditRule} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">תבניות מייל</h2>
            <p className="text-sm text-gray-500">תבניות לתגובה אוטומטית לפי סטטוסים עסקיים</p>
          </div>
          <Button onClick={() => setEditTemplate({
            id: 0,
            key: 'unknown_customer',
            subject: '',
            body_html: '',
            is_active: true,
          })}>
            <Plus className="h-4 w-4 ml-1" />
            תבנית חדשה
          </Button>
        </div>

        {templatesLoading ? <div className="flex justify-center py-10"><Spinner /></div> : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-right">מפתח</th>
                  <th className="px-4 py-3 text-right">נושא</th>
                  <th className="px-4 py-3 text-right">פעיל</th>
                  <th className="px-4 py-3 text-right">פעולות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {templates.map((template) => (
                  <TemplateRow key={template.id} template={template} onEdit={setEditTemplate} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editRule && <RuleDialog rule={editRule} onClose={() => setEditRule(null)} />}
      {editTemplate && <TemplateDialog template={editTemplate} onClose={() => setEditTemplate(null)} />}
    </div>
  );
}

function RuleRow({ rule, onEdit }: { rule: IncomingEmailRule; onEdit: (rule: IncomingEmailRule) => void }) {
  const [deleteRule] = useDeleteIncomingEmailRuleMutation();

  async function handleDelete() {
    try {
      await deleteRule(rule.id).unwrap();
      toast.success('הכלל נמחק');
    } catch {
      toast.error('שגיאה במחיקת כלל');
    }
  }

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">{rule.name}</td>
      <td className="px-4 py-3 text-xs text-gray-600">{rule.from_contains || '—'}</td>
      <td className="px-4 py-3 text-xs text-gray-600">{rule.subject_contains || '—'}</td>
      <td className="px-4 py-3">{rule.action === 'ignore' ? 'התעלם' : 'תגובה אוטומטית'}</td>
      <td className="px-4 py-3">{rule.priority}</td>
      <td className="px-4 py-3">{rule.is_active ? 'כן' : 'לא'}</td>
      <td className="px-4 py-3 flex gap-2">
        <Button size="sm" variant="outline" onClick={() => onEdit(rule)}><Pencil className="h-4 w-4" /></Button>
        <ConfirmPopover message={`למחוק את הכלל ${rule.name}?`} onConfirm={handleDelete}>
          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
        </ConfirmPopover>
      </td>
    </tr>
  );
}

function TemplateRow({ template, onEdit }: { template: EmailTemplateItem; onEdit: (template: EmailTemplateItem) => void }) {
  const [deleteTemplate] = useDeleteEmailTemplateMutation();

  async function handleDelete() {
    try {
      await deleteTemplate(template.id).unwrap();
      toast.success('התבנית נמחקה');
    } catch {
      toast.error('שגיאה במחיקת תבנית');
    }
  }

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">{template.key}</td>
      <td className="px-4 py-3">{template.subject}</td>
      <td className="px-4 py-3">{template.is_active ? 'כן' : 'לא'}</td>
      <td className="px-4 py-3 flex gap-2">
        <Button size="sm" variant="outline" onClick={() => onEdit(template)}><Pencil className="h-4 w-4" /></Button>
        <ConfirmPopover message={`למחוק את התבנית ${template.key}?`} onConfirm={handleDelete}>
          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
        </ConfirmPopover>
      </td>
    </tr>
  );
}

function RuleDialog({ rule, onClose }: { rule: IncomingEmailRule; onClose: () => void }) {
  const isNew = !rule.id;
  const [createRule, { isLoading: isCreating }] = useCreateIncomingEmailRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateIncomingEmailRuleMutation();
  const [form, setForm] = useState(rule);
  const isSaving = isCreating || isUpdating;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (isNew) {
        await createRule(form).unwrap();
      } else {
        await updateRule(form).unwrap();
      }
      toast.success('הכלל נשמר');
      onClose();
    } catch (err: unknown) {
      const msg = (err as { data?: { detail?: string } })?.data?.detail ?? 'שגיאה בשמירת כלל';
      toast.error(msg);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl p-6">
        <DialogHeader><DialogTitle>{isNew ? 'כלל חדש' : 'עריכת כלל'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label="שם" value={form.name} onChange={(value) => setForm((p) => ({ ...p, name: value }))} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="שולח מכיל" value={form.from_contains} onChange={(value) => setForm((p) => ({ ...p, from_contains: value }))} />
            <Field label="נושא מכיל" value={form.subject_contains} onChange={(value) => setForm((p) => ({ ...p, subject_contains: value }))} />
          </div>
          <Field label="תוכן מכיל" value={form.body_contains} onChange={(value) => setForm((p) => ({ ...p, body_contains: value }))} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>פעולה</Label>
              <Select value={form.action} onValueChange={(value) => setForm((p) => ({ ...p, action: value as IncomingEmailRule['action'] }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ignore">התעלם</SelectItem>
                  <SelectItem value="auto_reply">תגובה אוטומטית</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="מפתח תבנית" value={form.reply_template_key} onChange={(value) => setForm((p) => ({ ...p, reply_template_key: value }))} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <Field label="עדיפות" type="number" value={String(form.priority)} onChange={(value) => setForm((p) => ({ ...p, priority: Number(value || 0) }))} />
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(value) => setForm((p) => ({ ...p, is_active: value }))} />
              <Label>פעיל</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>ביטול</Button>
            <Button type="submit" disabled={isSaving}>{isSaving ? 'שומר...' : 'שמור'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TemplateDialog({ template, onClose }: { template: EmailTemplateItem; onClose: () => void }) {
  const isNew = !template.id;
  const [createTemplate, { isLoading: isCreating }] = useCreateEmailTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateEmailTemplateMutation();
  const [form, setForm] = useState(template);
  const isSaving = isCreating || isUpdating;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (isNew) {
        await createTemplate(form).unwrap();
      } else {
        await updateTemplate(form).unwrap();
      }
      toast.success('התבנית נשמרה');
      onClose();
    } catch (err: unknown) {
      const msg = (err as { data?: { detail?: string } })?.data?.detail ?? 'שגיאה בשמירת תבנית';
      toast.error(msg);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl p-6">
        <DialogHeader><DialogTitle>{isNew ? 'תבנית חדשה' : 'עריכת תבנית'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div className="space-y-1">
              <Label>מפתח</Label>
              <Select value={form.key} onValueChange={(value) => setForm((p) => ({ ...p, key: value as EmailTemplateItem['key'] }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown_customer">unknown_customer</SelectItem>
                  <SelectItem value="unknown_school">unknown_school</SelectItem>
                  <SelectItem value="closed_thread">closed_thread</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(value) => setForm((p) => ({ ...p, is_active: value }))} />
              <Label>פעיל</Label>
            </div>
          </div>
          <Field label="נושא" value={form.subject} onChange={(value) => setForm((p) => ({ ...p, subject: value }))} />
          <HtmlSignatureEditor value={form.body_html} onChange={(value) => setForm((p) => ({ ...p, body_html: value }))} label="תוכן HTML" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>ביטול</Button>
            <Button type="submit" disabled={isSaving}>{isSaving ? 'שומר...' : 'שמור'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
